// src/rtkQuery/features/hooks/useCancelOrderService.ts

import { useCallback } from 'react';
import { useCancelOrderMutation } from '@/rtkQuery/features/services/orders';
import type {
  CancelOrderRequest,
  CancelOrderResponse,
} from '@/rtkQuery/features/models/cancelOrder.model';
import { toastService } from '@/utils/toastService';

export const useCancelOrderService = () => {
  const [
    cancelOrder,
    {
      data,
      error,
      isLoading,
      isSuccess,
      isError,
      status,
    },
  ] = useCancelOrderMutation();

  const submitCancelOrder = useCallback(
    async (payload: CancelOrderRequest): Promise<CancelOrderResponse | undefined> => {
      try {
        const response = await cancelOrder(payload).unwrap();

          if (response?.Status === true) {
        toastService.success(response.Message || 'Cancellation request submitted successfully.');
      } else if (response?.Status === false) {
        toastService.error(response.Message || 'Unable to submit cancellation request.');
      } else {
        toastService.error('Unexpected response received from the server.');
      }
        return response;
      } catch (err) {
        toastService.error('Something went wrong while submitting the cancellation request.');
        return undefined;
      }
    },
    [cancelOrder]
  );

  const cancelOrderErrorMessage: string =
    (error as any)?.data?.message ||
    (error as any)?.error ||
    'Could not cancel the order.';

  return {
    submitCancelOrder,
    cancelOrderData: data,
    cancelOrderError: error,
    cancelOrderIsLoading: isLoading,
    cancelOrderIsSuccess: isSuccess,
    cancelOrderIsError: isError,
    cancelOrderStatus: status,
    cancelOrderErrorMessage,

    refundSuccessful: data?.RefundSuccessful ?? false,
    alreadyRefunded: data?.AlreadyRefunded ?? false,
    wasNewRefundCreated: !!data?.NewRefund,
    hadExistingRefund: !!data?.ExistingOrderRefund,
    magentoCancelStatus: data?.MagentoCancelOrder?.Status === 'true',
    magentoWasCanceled: data?.MagentoCancelOrder?.was_canceled ?? false,
    magentoOrderStatus: data?.MagentoCancelOrder?.order_status ?? null,
    cancelOrderMessage: data?.Message ?? '',
  };
};
